import React, { useState, useEffect, useRef } from 'react';
import { messageService } from '../../../services/messageService';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import MessageBubble from './MessageBubble';

const ChatInterface = ({ conversation, user, userProfile, onBack, isMobile }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const channelRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (conversation?.id && user?.id) {
      loadMessages();
      subscribeToNewMessages();
    }

    return () => {
      if (channelRef?.current) {
        channelRef?.current?.unsubscribe();
      }
    };
  }, [conversation?.id, user?.id]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const { data, error } = await messageService?.getMessages(conversation?.id);
      if (!error && data) {
        setMessages(data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNewMessages = () => {
    channelRef.current = messageService?.subscribeToMessages(conversation?.id, (newMessage) => {
      setMessages((prev) => {
        // Avoid duplicates
        if (prev?.some((msg) => msg?.id === newMessage?.id)) {
          return prev;
        }
        return [...prev, newMessage];
      });
      setIsTyping(false);
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage?.trim() || sending) return;

    try {
      setSending(true);
      let conversationId = conversation?.id;

      // If conversation does not exist, create it first
      if (!conversationId) {
        // Only customers can initiate new conversations
        if (userProfile?.role === 'customer') {
          // Find customerId and supplierId from props/context
          const customerId = userProfile?.customer_id || userProfile?.id;
          // Try to get supplierId from conversation?.otherParty or fallback (UI must provide)
          const supplierId = conversation?.otherParty?.id;
          if (!customerId || !supplierId) {
            throw new Error('Missing customer or supplier ID to create conversation.');
          }
          const { data: conv, error } = await messageService.getOrCreateConversation(customerId, supplierId);
          if (error || !conv) throw error || new Error('Failed to create conversation');
          conversationId = conv.id;
        } else {
          throw new Error('Only customers can initiate new conversations.');
        }
      }

      const { data, error } = await messageService?.sendMessage(
        conversationId,
        user?.id,
        newMessage,
        'text'
      );

      if (!error && data) {
        setNewMessage('');
        // Message will be added via real-time subscription
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || sending) return;

    try {
      setSending(true);
      // In a real implementation, upload to Supabase Storage first
      // For now, create a temporary URL
      const imageUrl = URL.createObjectURL(file);
      const imageAlt = `Image shared in conversation about custom order`;

      const { data, error } = await messageService?.sendMessage(
        conversation?.id,
        user?.id,
        'Shared an image',
        'image',
        imageUrl,
        imageAlt
      );

      if (error) {
        console.error('Error sending image:', error);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setSending(false);
      if (fileInputRef?.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleQuickReply = (text) => {
    setNewMessage(text);
  };

  const getOtherPartyName = () => {
    if (userProfile?.role === 'customer') {
      return conversation?.otherParty?.business_name || conversation?.otherParty?.user?.full_name || 'Supplier';
    }
    return conversation?.otherParty?.user?.full_name || 'Customer';
  };

  const getOtherPartyAvatar = () => {
    return conversation?.otherParty?.user?.avatar_url || 'https://randomuser.me/api/portraits/lego/1.jpg';
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border bg-card">
        {isMobile && (
          <button
            onClick={onBack}
            className="w-10 h-10 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
          >
            <Icon name="ArrowLeft" size={20} />
          </button>
        )}
        <div className="w-10 h-10 rounded-full overflow-hidden">
          <Image
            src={getOtherPartyAvatar()}
            alt={`${getOtherPartyName()} profile picture`}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-base">{getOtherPartyName()}</h3>
          <div className="flex items-center gap-2 text-xs text-success">
            <div className="w-2 h-2 bg-success rounded-full" />
            <span>Online</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background">
        {messages?.map((message) => (
          <MessageBubble
            key={message?.id}
            message={message}
            isOwnMessage={message?.sender_id === user?.id}
            senderAvatar={message?.sender?.avatar_url || getOtherPartyAvatar()}
            senderName={message?.sender?.full_name || getOtherPartyName()}
          />
        ))}

        {isTyping && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
              <Image
                src={getOtherPartyAvatar()}
                alt={`${getOtherPartyName()} profile picture`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="px-4 py-3 bg-muted rounded-lg">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Composition */}
      <div className="p-4 border-t border-border bg-card">
        <div className="flex gap-3 mb-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef?.current?.click()}
            disabled={sending}
            className="w-10 h-10 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center flex-shrink-0 disabled:opacity-50"
          >
            <Icon name="Paperclip" size={20} />
          </button>
          <Input
            type="text"
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            onKeyPress={(e) => e?.key === 'Enter' && !e?.shiftKey && handleSendMessage()}
            disabled={sending}
            className="flex-1"
          />
          <Button
            variant="default"
            size="icon"
            onClick={handleSendMessage}
            disabled={!newMessage?.trim() || sending}
            loading={sending}
          >
            <Icon name="Send" size={20} />
          </Button>
        </div>

        {/* Quick Reply Suggestions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleQuickReply('Can you customize this order?')}
            className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth"
          >
            Custom order request
          </button>
          <button
            onClick={() => handleQuickReply('What are the dietary options?')}
            className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth"
          >
            Dietary requirements
          </button>
          <button
            onClick={() => handleQuickReply('When can this be delivered?')}
            className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth"
          >
            Delivery timing
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;